import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Users, Pencil, Plus, Download, Building2, Store, History } from 'lucide-react';
import nurseryService from '../../services/nursery';
import nurseryAccountsService from '../../services/nurseryAccounts';
import nurseryRetailService from '../../services/nurseryRetail';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { useOrgTimezone, getTodayInTz, formatLocalDateInTz } from '../../utils/timezone';
import { addDays } from '../../utils/payPeriod';
import { errorMessage } from '../../utils/apiResponse';
import { downloadBlob } from '../../utils/downloadBlob';
import { isLegacyImportOpen } from '../../services/legacyImport';
import { money, num, termsLabel } from '../../utils/nurseryMoney';
import { PaymentStateBadge, SeatRequired, Tabs } from '../../components/nursery/accounts/AccountBits';
import XeroContactCard from '../../components/nursery/accounts/XeroContactCard';
import CustomerDepositsCard from '../../components/nursery/accounts/CustomerDepositsCard';
import CustomerCallsTab from '../../components/sales/CustomerCallsTab';
import CustomerNotesTab from '../../components/sales/CustomerNotesTab';
import { useCrmSeat } from '../../hooks/useCrmSeat';
import type { NurseryCustomer, NurseryOrder, NurseryPriceList } from '../../types/nursery';
import type { StorePickDays } from '../../types/nurseryRetail';
import type {
  CustomerAccount, CreditPosition, CustomerSite, CustomerPriceList, CustomerPriceListSummary, InvoiceDelivery, InvoiceRow, TermsSource,
} from '../../types/nurseryAccounts';

/**
 * /accounts/customers/:id — one customer: what it trades on, its sites, the
 * price list it is charged from, its orders and what it owes.
 *
 * A customer can be a site of a head office. The site keeps its own orders,
 * deliveries and invoices; the head office supplies the tier, terms and
 * prices unless the site sets its own, and (unless the site pays its own
 * bills) owes the money.
 */

type TabKey = 'details' | 'sites' | 'prices' | 'orders' | 'account' | 'notes' | 'calls';
const TAB_KEYS: TabKey[] = ['details', 'sites', 'prices', 'orders', 'account', 'notes', 'calls'];

const DELIVERY_LABELS: Record<InvoiceDelivery, string> = {
  email: 'Emailed',
  portal: 'Through the customer’s own portal — nothing is emailed, copies are kept here',
  none: 'Not sent',
};

const TERMS_SOURCE: Record<TermsSource, string> = {
  own: 'set on this customer',
  parent: 'from the head office',
  default: 'the default',
};

const cardCls = 'bg-white border border-neutral-200 rounded-xl p-4 sm:p-6';
const errorCls = 'bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words';
const linkCls = 'text-primary-600 hover:underline';
const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const withCode = (name: string | null | undefined, code: string | null | undefined) =>
  `${name || 'Unnamed customer'}${code ? ` (#${code})` : ''}`;

const statusLabel = (s: string) => {
  const t = s.replace(/_/g, ' ');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-neutral-500">{label}</dt>
      <dd className="text-sm text-neutral-800 break-words whitespace-pre-line">{children}</dd>
    </div>
  );
}

function Skeleton() {
  return <div className="h-40 bg-neutral-100 rounded-xl animate-pulse" />;
}

// ── Details ──

function DetailsTab({ customer }: { customer: NurseryCustomer }) {
  const account = customer.account ?? null;
  const isSite = Boolean(customer.parent_customer_id);
  const tz = useOrgTimezone();

  // The days a store with a number is picked for the cross dock, when a
  // schedule in force lists it. Most customers are on none, and then nothing
  // is shown.
  const [pickDays, setPickDays] = useState<StorePickDays['pick_days']>(null);
  useEffect(() => {
    setPickDays(null);
    if (!customer.customer_code) return;
    let cancelled = false;
    nurseryRetailService.getStorePickDays(customer.id, getTodayInTz(tz))
      .then((res) => { if (!cancelled && res.success) setPickDays(res.data?.pick_days ?? null); })
      .catch(() => { /* not on a schedule, or no seat that reads it */ });
    return () => { cancelled = true; };
  }, [customer.id, customer.customer_code, tz]);
  const sites = customer.site_count ?? 0;
  const fromHeadOffice = isSite ? ' (from the head office)' : '';

  const tier = customer.tier_name
    ? customer.tier_name
    : isSite && customer.parent_tier_name
      ? `${customer.parent_tier_name} (from the head office)`
      : 'No tier (base prices)';

  const paidByOther = account && account.payer_id !== customer.id;

  return (
    <div className="space-y-4">
      {(isSite || sites > 0) && (
        <div className={`${cardCls} text-sm text-neutral-700 space-y-1`}>
          {isSite && (
            <p className="flex items-start gap-2 min-w-0">
              <Store className="h-4 w-4 mt-0.5 shrink-0 text-neutral-400" />
              <span className="min-w-0 break-words">
                Site of{' '}
                <Link to={`/accounts/customers/${customer.parent_customer_id}`} className={linkCls}>
                  {withCode(customer.parent_name, customer.parent_code)}
                </Link>
                .
              </span>
            </p>
          )}
          {isSite && account && (
            <p className="min-w-0 break-words pl-6">
              Paid by{' '}
              {paidByOther
                ? <Link to={`/accounts/customers/${account.payer_id}?tab=account`} className={linkCls}>{account.payer_name}</Link>
                : <span className="font-medium">this site itself</span>}
              {paidByOther ? '.' : '. It keeps the head office’s prices and pays its own invoices.'}
            </p>
          )}
          {sites > 0 && (
            <p className="flex items-start gap-2 min-w-0">
              <Building2 className="h-4 w-4 mt-0.5 shrink-0 text-neutral-400" />
              <span className="min-w-0 break-words">
                Head office with{' '}
                <Link to={`/accounts/customers/${customer.id}?tab=sites`} className={linkCls}>
                  {sites} site{sites === 1 ? '' : 's'}
                </Link>
                .
              </span>
            </p>
          )}
        </div>
      )}

      <div className={cardCls}>
        <h2 className="text-base font-semibold text-neutral-800 mb-3">Details</h2>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Name">{customer.name}</Field>
          <Field label="Store or account number">{customer.customer_code || 'None'}</Field>
          {customer.customer_number && <Field label="Legacy customer number">{customer.customer_number}</Field>}
          <Field label="Contact">{customer.contact_name || 'None'}</Field>
          <Field label="Email">{customer.email || 'None'}</Field>
          <Field label="Phone">{customer.phone || 'None'}</Field>
          <Field label="ABN/ACN">{customer.abn || 'None'}</Field>
          <Field label="Default delivery address">{customer.delivery_address || 'None — pickup only'}</Field>
          <Field label="Notes">{customer.notes || 'None'}</Field>
        </dl>
      </div>

      {(customer.contacts?.length ?? 0) > 0 && (
        <div className={cardCls}>
          <h2 className="text-base font-semibold text-neutral-800 mb-2">Contacts</h2>
          <ul className="divide-y divide-neutral-100">
            {customer.contacts!.map((c) => (
              <li key={c.id} className="py-2 text-sm min-w-0">
                <p className="font-medium text-neutral-800 break-words">
                  {[c.title, c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || 'No name'}
                  {c.is_main ? ' (main)' : ''}
                  {c.position ? <span className="font-normal text-neutral-500">{` · ${c.position}`}</span> : null}
                </p>
                <p className="text-neutral-600 break-words">
                  {[c.email, c.phone && `Phone ${c.phone}`, c.mobile && `Mobile ${c.mobile}`, c.after_hours && `After hours ${c.after_hours}`]
                    .filter(Boolean).join(' · ') || 'No details'}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={cardCls}>
        <h2 className="text-base font-semibold text-neutral-800">Account</h2>
        <p className="text-sm text-neutral-500 mb-3">
          What this customer trades on{isSite ? ', counting what it takes from its head office' : ''}.
        </p>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Pricing tier">{tier}</Field>
          <Field label="Payment terms">
            {account?.terms ? `${termsLabel(account.terms)} (${TERMS_SOURCE[account.terms.source]})` : termsLabel(null)}
          </Field>
          <Field label="Purchase order number">
            {account
              ? `${account.po_required ? 'Required on every order' : 'Not required'}${customer.po_required == null ? fromHeadOffice : ''}`
              : 'Not known'}
          </Field>
          <Field label="Credit limit (inc GST)">
            {account
              ? `${account.credit_limit != null ? money(account.credit_limit) : 'None'}${paidByOther ? ` (belongs to ${account.payer_name}, who pays)` : ''}`
              : 'Not known'}
          </Field>
          {account?.pay_before_despatch !== undefined && (
            <Field label="Payment">
              {account.pay_before_despatch
                ? `Pays before despatch${paidByOther ? ` (set on ${account.payer_name}, who pays)` : ''}`
                : 'On account'}
            </Field>
          )}
          <Field label="How invoices are delivered">
            {account
              ? `${DELIVERY_LABELS[account.invoice_delivery] ?? 'Not known'}${customer.invoice_delivery == null ? fromHeadOffice : ''}`
              : 'Not known'}
          </Field>
          <Field label="Despatched from">
            {account?.despatch_farm_name
              ? `${account.despatch_farm_name}${customer.despatch_farm_id == null ? fromHeadOffice : ''}`
              : 'No farm set'}
          </Field>
          {account?.uses_cross_dock === true ? (
            <Field label="Cross dock">
              {account.cross_dock_name
                ? `${account.cross_dock_name}${customer.cross_dock_id == null ? fromHeadOffice : ''}`
                : `The one taking its state${customer.uses_cross_dock == null ? fromHeadOffice : ''}`}
            </Field>
          ) : account?.uses_cross_dock === undefined && account?.cross_dock_name ? (
            <Field label="Cross dock">
              {`${account.cross_dock_name}${customer.cross_dock_id == null ? fromHeadOffice : ''}`}
            </Field>
          ) : null}
          {pickDays && (
            <Field label="Cross dock pick days">
              {pickDays.days.map((d) => `${d.day_name}${d.route ? ` (${d.route})` : ''}`).join(', ')}
              {` · ${pickDays.schedule_name}`}
            </Field>
          )}
        </dl>
      </div>
    </div>
  );
}

// ── Sites ──

function SitesTab({ customer, canEdit }: { customer: NurseryCustomer; canEdit: boolean }) {
  const navigate = useNavigate();
  const [sites, setSites] = useState<CustomerSite[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await nurseryAccountsService.getSites(customer.id);
        if (!live) return;
        if (res.success) setSites(res.data || []);
        else setError(errorMessage(res, 'Could not load the sites'));
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not load the sites'));
      }
    })();
    return () => { live = false; };
  }, [customer.id]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm text-neutral-500 min-w-0 flex-1 basis-60">
          A site is a store or branch of this customer. It gets its own deliveries and its own invoices, on this
          head office&rsquo;s prices and terms.
        </p>
        {canEdit && (
          <Button size="sm" onClick={() => navigate(`/accounts/customers/new?parent_id=${customer.id}`)}>
            <Plus className="h-4 w-4" /> Add a site
          </Button>
        )}
      </div>

      {error && <div className={errorCls}>{error}</div>}

      {error ? null : sites === null ? <Skeleton /> : sites.length === 0 ? (
        <div className={`${cardCls} text-center text-sm text-neutral-500`}>
          This customer has no sites. Add one for each store or branch that takes its own deliveries, so each
          gets its own invoice while the head office pays for them all.
        </div>
      ) : (
        <ul className="space-y-2">
          {sites.map((s) => (
            <li key={s.id}>
              <Link to={`/accounts/customers/${s.id}`} className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
                <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                  <div className="min-w-0 flex-1 basis-48">
                    <p className="font-medium text-neutral-900 break-words">{s.name}</p>
                    <p className="text-xs text-neutral-500 break-words">
                      {s.customer_code ? `Number ${s.customer_code}` : 'No store number'}
                      {' · '}
                      {s.bills_to_parent ? 'Head office pays its invoices' : 'Pays its own invoices'}
                    </p>
                    {!s.active && (
                      <span className="mt-1 inline-flex rounded-full bg-neutral-100 text-neutral-600 px-2 py-0.5 text-xs font-medium">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-neutral-500">Owed inc GST</p>
                    <p className="text-sm font-semibold text-neutral-900">{money(s.balance)}</p>
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Prices ──

function PricesTab({ customer, canEdit }: { customer: NurseryCustomer; canEdit: boolean }) {
  const tz = useOrgTimezone();
  const [info, setInfo] = useState<CustomerPriceList | null>(null);
  // Every list that can be chosen. Archived lists price nothing and are not offered.
  const [choices, setChoices] = useState<NurseryPriceList[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [res, all] = await Promise.all([
        nurseryAccountsService.getCustomerPriceList(customer.id),
        canEdit ? nurseryService.getPriceLists().catch(() => null) : Promise.resolve(null),
      ]);
      if (!res.success || !res.data) { setError(errorMessage(res, 'Could not load the price list')); return; }
      setInfo(res.data);
      setChoices(all?.success ? (all.data || []).filter((l) => l.status !== 'archived') : []);
    } catch (e) {
      setError(errorMessage(e, 'Could not load the price list'));
    }
  }, [customer.id, canEdit]);

  useEffect(() => { load(); }, [load]);

  const choose = async (value: string) => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await nurseryService.updateCustomer(customer.id, { price_list_id: value ? Number(value) : null });
      if (!res.success) { setSaveError(errorMessage(res, 'Could not change the price list')); return; }
      await load();
    } catch (e) {
      setSaveError(errorMessage(e, 'Could not change the price list'));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className={errorCls}>{error}</div>;
  if (!info) return <Skeleton />;

  const hasParent = Boolean(customer.parent_customer_id);
  const parentName = info.parent_name || 'the head office';
  const fallbackLabel = hasParent
    ? `Same as ${parentName}`
    : info.default_list ? `Default list (${info.default_list.name})` : 'Default list (none set)';

  const where = info.source === 'customer'
    ? 'Chosen for this customer.'
    : info.source === 'parent'
      ? `Chosen for ${parentName}, so this site is priced from it too.`
      : 'The default list: no other list is chosen for this customer.';

  const notInForce = (l: CustomerPriceListSummary) => {
    if (l.status !== 'active') return `It is a ${l.status}, so it prices nothing until it is made active.`;
    if (l.effective_from.slice(0, 10) > getTodayInTz(tz)) return `It starts ${formatLocalDateInTz(l.effective_from, tz)} and prices nothing until then.`;
    if (l.effective_to) return `It ended ${formatLocalDateInTz(l.effective_to, tz)} and prices nothing now.`;
    return 'It is not in force today.';
  };

  const list = info.list;

  return (
    <div className="space-y-4">
      <p className="text-sm text-neutral-500 break-words">
        A customer is priced from one list. Unless another is chosen here it is on
        {hasParent ? ' its head office’s list, else' : ''} the default list, set on{' '}
        <Link to="/accounts/price-lists" className={linkCls}>Price lists</Link>.
      </p>

      {canEdit && (
        <div className={cardCls}>
          <label htmlFor="cust-price-list" className="block text-sm font-medium text-neutral-700 mb-1">Price list</label>
          <select
            id="cust-price-list"
            value={info.price_list_id ?? ''}
            disabled={saving}
            onChange={(e) => choose(e.target.value)}
            className={inputCls}
          >
            <option value="">{fallbackLabel}</option>
            {choices.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}{l.status === 'draft' ? ' (draft)' : ''}{l.is_default ? ' (default)' : ''}
              </option>
            ))}
          </select>
          {saveError && <p className="text-sm text-red-600 mt-2 break-words">{saveError}</p>}
        </div>
      )}

      {list ? (
        <div className={cardCls}>
          <p className="text-xs font-medium text-neutral-500">Priced from</p>
          {canEdit
            ? <Link to={`/accounts/price-lists/${list.id}/edit`} className={`${linkCls} font-medium break-words`}>{list.name}</Link>
            : <p className="font-medium text-neutral-800 break-words">{list.name}</p>}
          <p className="text-sm text-neutral-700 break-words">{where}</p>
          <p className="text-xs text-neutral-500 break-words">
            {formatLocalDateInTz(list.effective_from, tz)}
            {list.effective_to ? ` to ${formatLocalDateInTz(list.effective_to, tz)}` : ' onwards'}
            {` · ${num(list.item_count)} price${num(list.item_count) === 1 ? '' : 's'}`}
          </p>
          {!list.in_force && (
            <p className="text-sm text-amber-800 mt-2 break-words">
              {notInForce(list)} Order lines start with no price until it does.
            </p>
          )}
          <p className="text-xs text-neutral-500 mt-2 break-words">
            A tree this list does not price starts with no price on an order; no other list is looked at.
          </p>
        </div>
      ) : (
        <div className={`${cardCls} text-sm text-neutral-600 break-words`}>
          No list is chosen and no default list is set, so an order line starts with no price and one is typed in.
        </div>
      )}
    </div>
  );
}

// ── Orders ──

function OrdersTab({ customer }: { customer: NurseryCustomer }) {
  const tz = useOrgTimezone();
  const [orders, setOrders] = useState<NurseryOrder[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await nurseryService.getOrders({ customer_id: customer.id });
        if (!live) return;
        if (res.success) setOrders(res.data || []);
        else setError(errorMessage(res, 'Could not load the orders'));
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not load the orders'));
      }
    })();
    return () => { live = false; };
  }, [customer.id]);

  if (error) return <div className={errorCls}>{error}</div>;
  if (orders === null) return <Skeleton />;
  if (orders.length === 0) {
    return <div className={`${cardCls} text-center text-sm text-neutral-500`}>No orders have been placed for this customer yet.</div>;
  }

  return (
    <ul className="space-y-2">
      {orders.map((o) => (
        <li key={o.id}>
          <Link to={`/nursery/orders/${o.id}`} className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-semibold text-neutral-900 break-words min-w-0">{o.order_number}</span>
              <span className="inline-flex rounded-full bg-neutral-100 text-neutral-700 px-2 py-0.5 text-xs font-medium">
                {statusLabel(o.status)}
              </span>
              <span className="ml-auto text-sm font-medium text-neutral-800">{money(o.total_value)} <span className="text-xs font-normal text-neutral-500">ex GST</span></span>
            </div>
            <p className="mt-1 text-xs text-neutral-500 break-words">
              {formatLocalDateInTz(String(o.order_date).slice(0, 10), tz)}
              {o.customer_po ? ` · PO ${o.customer_po}` : ' · No PO number'}
            </p>
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ── Account ──

type AccountData = CustomerAccount & { credit: CreditPosition | null };

function AccountTab({ customer, isAccounts }: { customer: NurseryCustomer; isAccounts: boolean }) {
  const tz = useOrgTimezone();
  const today = getTodayInTz(tz);
  const [account, setAccount] = useState<AccountData | null>(null);
  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [from, setFrom] = useState(() => addDays(today, -90));
  const [to, setTo] = useState(today);
  const [downloading, setDownloading] = useState<'statement' | 'copies' | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await nurseryAccountsService.getAccount(customer.id);
        if (!live) return;
        if (!res.success || !res.data) {
          setError(errorMessage(res, 'Could not load the account'));
          return;
        }
        setAccount(res.data);
        // A payer's invoices are everything it owes for, its sites included.
        // A site paid for by its head office lists only its own.
        const inv = await nurseryAccountsService.getInvoices(
          res.data.pays_for_itself ? { payer_id: customer.id } : { customer_id: customer.id },
        );
        if (!live) return;
        if (inv.success) setInvoices(inv.data || []);
        else setError(errorMessage(inv, 'Could not load the invoices'));
      } catch (e) {
        if (live) setError(errorMessage(e, 'Could not load the account'));
      }
    })();
    return () => { live = false; };
  }, [customer.id]);

  const download = async (what: 'statement' | 'copies') => {
    if (!account) return;
    setDownloading(what);
    setDownloadError(null);
    try {
      const range = { from, to };
      const blob = what === 'statement'
        ? await nurseryAccountsService.getStatementPdf(account.payer_id, range)
        : await nurseryAccountsService.getInvoiceCopiesPdf(account.payer_id, range);
      const ref = customer.customer_code || String(account.payer_id);
      downloadBlob(blob, `${what === 'statement' ? 'statement' : 'invoices'}-${ref}-${from}-to-${to}.pdf`);
    } catch (e) {
      setDownloadError(errorMessage(e, what === 'statement' ? 'Could not make the statement' : 'Could not make the invoice copies'));
    } finally {
      setDownloading(null);
    }
  };

  if (error && !account) return <div className={errorCls}>{error}</div>;
  if (!account) return <Skeleton />;

  const credit = account.credit;
  const paysItself = account.pays_for_itself;
  const overdue30 = num(credit?.overdue ?? credit?.overdue_30);
  const rangeOk = Boolean(from && to && from <= to);

  return (
    <div className="space-y-4">
      {!paysItself && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-900 break-words">
          Invoices for this site are paid by{' '}
          <Link to={`/accounts/customers/${account.payer_id}?tab=account`} className="underline font-medium">
            {account.payer_name}
          </Link>
          . The figures below are that account&rsquo;s, and statements are made from its page.
        </div>
      )}

      {credit && (credit.over_limit || overdue30 > 0) && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-900 break-words space-y-1">
          {credit.over_limit && (
            <p>
              {credit.payer_name} owes {money(credit.balance)} inc GST, which is over its credit limit of{' '}
              {money(credit.credit_limit)}.
            </p>
          )}
          {overdue30 > 0 && (
            <p>{money(overdue30)} has been overdue for more than {credit.overdue_days ?? 30} days (inc GST).</p>
          )}
          <p>
            {!credit.credit_action || credit.credit_action === 'warn'
              ? 'This is a warning only. Orders can still be taken and sent.'
              : credit.credit_action === 'hold'
                ? 'Its orders are held at despatch until they are paid or released.'
                : 'A new order cannot be confirmed by hand until it is released, and its orders are held at despatch.'}{' '}
            <Link to="/accounts/account-rules" className={linkCls}>Account rules</Link>
          </p>
        </div>
      )}

      <div className={cardCls}>
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-3">
          <Field label={paysItself ? 'Balance owed (inc GST)' : `Balance owed by ${account.payer_name} (inc GST)`}>
            <span className="text-lg font-semibold">{credit ? money(credit.balance) : 'Not known'}</span>
          </Field>
          <Field label="Credit limit (inc GST)">
            {(credit?.credit_limit ?? account.credit_limit) != null ? money(credit?.credit_limit ?? account.credit_limit) : 'None'}
          </Field>
          <Field label="Payment terms">{termsLabel(account.terms)}</Field>
        </dl>
        {isAccounts && (
          <p className="mt-3 text-sm">
            <Link to={`/accounts/rebates?customer_id=${account.payer_id}`} className={linkCls}>Rebate agreements</Link>
          </p>
        )}
      </div>

      {paysItself && isAccounts && <XeroContactCard customerId={customer.id} customerName={customer.name} />}

      {isAccounts && <CustomerDepositsCard customerId={customer.id} />}

      {paysItself && (
        <div className={cardCls}>
          <h2 className="text-base font-semibold text-neutral-800">Statement</h2>
          <p className="text-sm text-neutral-500 mb-3">
            Everything invoiced, credited and paid on this account between two dates{(customer.site_count ?? 0) > 0 ? ', for all of its sites' : ''}.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md">
            <div>
              <label htmlFor="statement-from" className="block text-sm font-medium text-neutral-700 mb-1">From</label>
              <input id="statement-from" type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="statement-to" className="block text-sm font-medium text-neutral-700 mb-1">To</label>
              <input id="statement-to" type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={inputCls} />
            </div>
          </div>
          {!rangeOk && <p className="text-xs text-red-600 mt-2">Choose a from date that is on or before the to date.</p>}
          {downloadError && <div className={`${errorCls} mt-3`}>{downloadError}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={() => download('statement')} disabled={!rangeOk || downloading !== null}>
              <Download className="h-4 w-4" /> {downloading === 'statement' ? 'Making the statement…' : 'Download statement'}
            </Button>
            <Button variant="outline" size="sm" onClick={() => download('copies')} disabled={!rangeOk || downloading !== null}>
              <Download className="h-4 w-4" /> {downloading === 'copies' ? 'Making the copies…' : 'Download invoice copies'}
            </Button>
          </div>
        </div>
      )}

      <section className="space-y-2">
        <h2 className="text-base font-semibold text-neutral-800">
          {paysItself ? 'Invoices' : 'This site’s invoices'}
        </h2>
        {error && <div className={errorCls}>{error}</div>}
        {error ? null : invoices === null ? <Skeleton /> : invoices.length === 0 ? (
          <div className={`${cardCls} text-center text-sm text-neutral-500`}>
            No invoices yet. One is raised when an order is picked.
          </div>
        ) : (
          <ul className="space-y-2">
            {invoices.map((inv) => (
              <li key={inv.id}>
                <Link to={`/accounts/invoices/${inv.id}`} className="block bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <span className="font-semibold text-neutral-900 break-words min-w-0">{inv.invoice_number}</span>
                    <PaymentStateBadge state={inv.payment_state} />
                    <span className="ml-auto text-sm text-neutral-800">
                      <span className="text-neutral-500">Total inc GST</span> {money(inv.total_amount)}
                      {' · '}
                      <span className="text-neutral-500">Owing inc GST</span> <span className="font-semibold">{money(inv.balance)}</span>
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-700 break-words">{withCode(inv.customer_name, inv.customer_code)}</p>
                  <p className="text-xs text-neutral-500 break-words">
                    {inv.customer_po ? `PO ${inv.customer_po}` : 'No PO number'}
                    {` · Invoiced ${formatLocalDateInTz(String(inv.invoice_date).slice(0, 10), tz)}`}
                    {inv.due_date ? ` · Due ${formatLocalDateInTz(String(inv.due_date).slice(0, 10), tz)}` : ''}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

// ── Page ──

function CustomerDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const customerId = Number(id);
  const [params, setParams] = useSearchParams();
  // Setting a customer up is an accounts job. Every Nursery and Retail seat,
  // pickers included, reads the customer and its account, and takes cash for
  // its invoices (Rob, 2026-10-13). Prices stay with a nursery manager or admin.
  const { hasAccess: canEdit, loading: accountsLoading } = useModuleLicense('accounts', ['user', 'admin']);
  const { hasAccess: canGrow, loading: nurseryLoading } = useModuleLicense('nursery', ['manager', 'admin']);
  const stock = useStockSeat('count');
  // TEMPORARY (gone after 2026-10-28): fill this customer from hh.factree.
  const { hasAccess: isNurseryAdmin } = useModuleLicense('nursery', ['admin']);
  // Calls, voicemails, notes and follow-ups are the Sales module's (a Sales seat), shown here so they sit with the customer.
  const { hasAccess: hasSales } = useModuleLicense('sales');
  // Notes, follow-ups and issues: a Sales seat, or a Nursery operator and up.
  const { hasAccess: hasCrm } = useCrmSeat();
  // Sales adds and edits customers, their sites and price list too (Rob,
  // 2026-10-19); the Account tab's money stays with Accounts (canEdit).
  const canEditCustomer = canEdit || hasSales;
  const canSyncLegacy = (canEdit || isNurseryAdmin) && isLegacyImportOpen();
  const hasAccess = canEdit || stock.hasAccess;
  const canSeePrices = canEdit || canGrow || hasSales;
  const licenseLoading = accountsLoading || nurseryLoading || stock.loading;

  const [customer, setCustomer] = useState<NurseryCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setCustomer(null);
    try {
      const res = await nurseryService.getCustomer(customerId);
      if (res.success && res.data) setCustomer(res.data);
      else setError(errorMessage(res, 'Could not load this customer'));
    } catch (e) {
      setError(errorMessage(e, 'Could not load this customer'));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (hasAccess) load();
  }, [hasAccess, load]);

  if (licenseLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="h-8 bg-neutral-100 rounded animate-pulse w-64 mb-4" />
        <div className="h-40 bg-neutral-100 rounded animate-pulse" />
      </div>
    );
  }

  if (!hasAccess) return <SeatRequired seat="user" what="A customer&rsquo;s account" />;

  const isSite = Boolean(customer?.parent_customer_id);
  const requested = params.get('tab') as TabKey | null;
  // A site has no sites of its own, so that tab is not offered.
  const allowed = TAB_KEYS.filter((k) => !(k === 'sites' && isSite) && !(k === 'prices' && !canSeePrices) && !(k === 'calls' && !hasSales) && !(k === 'notes' && !hasCrm));
  const tab: TabKey = requested && allowed.includes(requested) ? requested : 'details';

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'details', label: 'Details' },
    ...(isSite ? [] : [{ key: 'sites' as const, label: 'Sites', count: customer?.site_count ?? 0 }]),
    ...(canSeePrices ? [{ key: 'prices' as const, label: 'Prices' }] : []),
    { key: 'orders', label: 'Orders' },
    { key: 'account', label: 'Account' },
    ...(hasCrm ? [{ key: 'notes' as const, label: 'Notes' }] : []),
    ...(hasSales ? [{ key: 'calls' as const, label: 'Calls' }] : []),
  ];

  const subtitle = customer
    ? [
      customer.customer_code ? `Number ${customer.customer_code}` : null,
      isSite ? `Site of ${customer.parent_name || 'a head office'}` : null,
      !isSite && (customer.site_count ?? 0) > 0 ? 'Head office' : null,
      customer.active === false ? 'Inactive' : null,
    ].filter(Boolean).join(' · ') || 'Customer'
    : 'Customer';

  return (
    <>
      <PageHeader
        title={customer?.name ?? 'Customer'}
        subtitle={subtitle}
        icon={<Users className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/accounts/customers')}>All customers</Button>
            {customer && canSyncLegacy && (
              <Button variant="outline" size="sm" onClick={() => navigate(`/nursery/legacy-import?customer=${customer.id}`)}>
                <History className="h-4 w-4" /> Sync from legacy
              </Button>
            )}
            {customer && canEditCustomer && (
              <Button size="sm" onClick={() => navigate(`/accounts/customers/${customer.id}/edit`)}>
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
        {error && <div className={errorCls}>{error}</div>}
        {loading ? <Skeleton /> : customer && (
          <>
            <Tabs
              label="Customer sections"
              tabs={tabs}
              value={tab}
              onChange={(key) => setParams(key === 'details' ? {} : { tab: key }, { replace: true })}
            />
            {/* Keyed on the customer so moving from a site to its head office starts each tab afresh. */}
            {tab === 'details' && <DetailsTab key={customer.id} customer={customer} />}
            {tab === 'sites' && <SitesTab key={customer.id} customer={customer} canEdit={canEditCustomer} />}
            {tab === 'prices' && <PricesTab key={customer.id} customer={customer} canEdit={canEditCustomer} />}
            {tab === 'orders' && <OrdersTab key={customer.id} customer={customer} />}
            {tab === 'account' && <AccountTab key={customer.id} customer={customer} isAccounts={canEdit} />}
            {tab === 'notes' && <CustomerNotesTab key={customer.id} customerId={customer.id} />}
            {tab === 'calls' && <CustomerCallsTab key={customer.id} customerId={customer.id} />}
          </>
        )}
      </div>
    </>
  );
}

export default CustomerDetail;
